import uuidv4 from "uuid-random";
import {
	Channel,
	DisplayMetadata,
	DesktopAgent,
	Context,
	ContextHandler,
	Listener,
	AppIntent,
	IntentResolution,
	ImplementationMetadata,
	TargetApp,
	OpenError,
	ResolveError,
} from "../../typedefs/FDC3";
import Logger from "../logger";
import { IRouterClient } from "../routerClient";
import { Startup } from "../Startup/Startup";
import { StandardCallback, StandardError } from "../../types";
import ConfigClient from "../configClient";
import SystemManagerClient from "../../common/systemManagerClient";
import { Globals } from "../../common/Globals";
import { SERVER_CHANNEL } from "./types";
import {
	InteropMeta,
	InteropMessage,
	InteropRegister,
	InteropUnregister,
	InteropBroadcast,
	InteropSystemChannelRequest,
	InteropCurrentContextRequest,
	InteropOpenRequest,
	InteropOpenResponse,
	InteropFindIntentRequest,
	InteropFindIntentResponse,
	InteropFindIntentsByContext,
	InteropIntentsList,
	InteropIntent,
	InteropRaiseIntent,
	InteropRaiseIntentResponse,
	InteropSubscribeContext,
	InteropUnsubscribeContext,
	InteropJoinChannels,
	InteropLeaveChannels,
	InteropSubscribeIntent,
	InteropUnsubscribeIntent,
	InteropGetCurrentChannels,
	InteropGetCurrentChannelsResponse,
	MultiMap,
	mmAdd,
	mmRemove,
	errorAsString,
} from "./types";

const Uninitialized = "";

/**
 * Heartbeats are used by the InteropService to detect dead fdc3 client connections. A connection
 * can die if a window crashes or is terminated. Heartbeats are also currently the only way to detect
 * that freestanding apps have closed, even gracefully.
 */
class HeartbeatMonitor {
	// If Finsemble sleeps (power saving mode) then heartbeats will slow to once per minute (this is a browser thing)
	// So we only declare an app dead if a heartbeat isn't received for two minutes, even though normally we should
	// expect to receive one every 10 seconds. Hopefully in the future we will have more reliable, router disconnection
	// capabilities based on underlying transports such as WebSocket.
	#declareDead = 2 * 60 * 1000;

	#timeoutHandle: any = null;

	// Contains onDisconnect()'s registered handlers
	#disconnectHandlers: Array<(error: string) => void> = [];

	start() {
		this.registerHeartbeat();
	}

	registerHeartbeat() {
		// Got a heartbeat, so clear our death timer
		clearTimeout(this.#timeoutHandle);
		Logger.system.debug(`FinsembleDesktopAgent: heartbeat received`);

		// And start a new one
		this.#timeoutHandle = setTimeout(() => {
			Logger.system.warn(`FinsembleDesktopAgent: No heartbeat received within ${this.#declareDead}  milliseconds`);
			this.notifyDisconnect();
		}, this.#declareDead);
	}

	notifyDisconnect() {
		console.warn("fdc3 has lost connectivity to the interop service. You may need to restart this app.");
		this.#disconnectHandlers.forEach((handler) => {
			handler(`No heartbeat received from interop service within ${this.#declareDead}  milliseconds`);
		});
	}

	onDisconnect(cb: (error: string) => void) {
		this.#disconnectHandlers = this.#disconnectHandlers.concat(cb);
	}

	stop() {
		clearTimeout(this.#timeoutHandle);
	}
}

/**
 * Implements FDC3's Channel interface. All actual transmissions still occur through the desktop agent via private functions.
 */
class ChannelProxy implements Channel {
	#desktopAgent;

	constructor(desktopAgent: FinsembleDesktopAgent, id: string, type: string = "app") {
		this.#desktopAgent = desktopAgent;
		this.id = id;
		this.type = type;
	}

	readonly id: string;

	/**
	 * Uniquely defines each channel type.
	 */
	readonly type: string;

	/**
	 * Channels may be visualized and selectable by users. DisplayMetadata may be used to provide hints on how to see them.
	 * For app channels, displayMetadata would typically not be present
	 */
	readonly displayMetadata?: DisplayMetadata;

	/**
	 * Broadcasts the given context on this channel. This is equivalent to joining the channel and then calling the
	 * top-level FDC3 `broadcast` function.
	 *
	 * Note that this function can be used without first joining the channel, allowing applications to broadcast on
	 * channels that they aren't a member of.
	 *
	 * `Error` with a string from the `ChannelError` enumeration.
	 */
	broadcast(context: Context): void {
		if (!context) {
			throw new Error("Invalid arguments. The required context parameter is missing.");
		}
		this.#desktopAgent.channelBroadcast(this.id, context);
	}

	/**
	 * Returns the last context that was broadcast on this channel. All channels initially have no context, until a
	 * context is broadcast on the channel. If there is not yet any context on the channel, this method
	 * will return `null`.
	 *
	 * The context of a channel will be captured regardless of how the context is broadcasted on this channel - whether
	 * using the top-level FDC3 `broadcast` function, or using the channel-level {@link broadcast} function on this
	 * object.
	 *
	 * Optionally a {@link contextType} can be provided, in which case the current context of the matching type will
	 * be returned (if any). Desktop agent implementations may decide to record contexts by type, in which case it will
	 * be possible to get the most recent context of the type specified, but this is not guaranteed.
	 *
	 * `Error` with a string from the `ChannelError` enumeration.
	 */
	getCurrentContext(contextType?: string): Promise<Context | null> {
		return this.#desktopAgent.channelGetCurrentContext(this.id, contextType);
	}

	/**
	 * Adds a listener for incoming contexts whenever a broadcast happens on this channel.
	 */
	addContextListener(contextTypeOrHandler: string | null | ContextHandler, handler?: ContextHandler): Listener {
		if (contextTypeOrHandler === undefined) {
			throw new Error("Invalid arguments. The contextTypeOrHandler parameter shouldn't be undefined.");
		}

		if (typeof contextTypeOrHandler !== "function" && !handler) {
			throw new Error("Invalid arguments. The required handler parameter is missing.");
		}

		return this.#desktopAgent.channelAddContextListener(this.id, contextTypeOrHandler, handler);
	}
}

type ExchangeResolve = ({ err, receiveMessage }: { err?: StandardError; receiveMessage?: InteropMessage }) => void;

/**
 * See https://stackoverflow.com/questions/45847399/friend-class-in-typescript for implementation of "friend" class
 */
export class FinsembleDesktopAgent implements DesktopAgent {
	#connected = false;

	#heartbeatMonitor: HeartbeatMonitor = new HeartbeatMonitor();

	// A lock that prevents start() from running multiple times
	#startHasBeenCalled = false;

	// uuid is set in connect(), when startup.onReady
	#uuid: string = "";

	#clientChannel = `FSBL.FDC3.Client.${uuidv4()}`;

	#intentHandlers: MultiMap<ContextHandler> = {};

	#contextHandlers: MultiMap<{
		handler: ContextHandler;
		channel?: string;
		targetSubscriberId?: string;
	}> = {};

	// Stores fdc3 calls that are received before a connection is established with the interop service
	// Once a connection is established then the queue is released.
	#queue: { message: InteropMessage; handler?: StandardCallback; resolve: ExchangeResolve }[] = [];

	#routerClient: IRouterClient;

	#startup: Startup;

	#FSBLVersion: string = Uninitialized;

	constructor({ routerClient, startup }: { routerClient: IRouterClient; startup: Startup }) {
		this.#routerClient = routerClient;
		this.#startup = startup;

		this.cacheFSBLVersion();

		// Per 1.2 spec, dispatch this event as soon as the global fdc3 object exists and is ready to accept API calls.
		// We should not wait until start() because in some circumstances, start() is not called *until* an API call is made.
		// Skip this if the global Event class is undefined (if running in Node.js such as a unit test). Note: also needed to confirm window.dispatchEvent was available, perhaps because of latest Node version.
		if (Globals.Event && Globals.dispatchEvent) {
			const event = new Event("fdc3Ready");
			Globals.dispatchEvent(event);
		}
	}

	// Cache FSBL version for use by fdc3.getInfo.
	async cacheFSBLVersion() {
		// Since setFSBLVersion is async, there is a timing gap until FDC3 2.0 supports async getInfo(). However, the default for #FSBLVersion covers the gap for now.
		// It's possible to close this gap now by not dispatching "fdc3Ready" until FSBLVersion is set, but doing that properly requires a change to the fdc3 preload and
		// then breaks existing apps that don't wait as defined in 1.2 spec.
		if (this.#FSBLVersion == Uninitialized) {
			this.#FSBLVersion = "6.6.0";
		}
		const { err, data: FSBLVersion } = await ConfigClient.getValue({ field: "finsemble.system.FSBLVersion" });
		if (!err) {
			this.#FSBLVersion = FSBLVersion;
		}
	}

	private fdc3Error(msg: string): Error {
		return new Error(msg);
	}

	private meta(): InteropMeta {
		return {
			uuid: this.#uuid,
			href: window.document ? document.location.href : "",
		};
	}

	private handleHeartbeat() {
		this.#heartbeatMonitor.registerHeartbeat();
	}

	private triggerContextListeners(message: InteropBroadcast) {
		const { context } = message.payload;
		let handlers;

		// The targetSubscriberId field is set only when contexts are from an autoBroadcast. During autoBroadcast, one context is received for a specific
		// subscriber (for addContextListener), or multiple contexts may be received in rapid fire (for joinChannel with existing subscribers).
		// For all cases only the handler identified by targetSubscriberId is triggered.
		//
		// The need for targetSubscriberId is due to a channel having multiple listeners (based on context), with each of these context listeners potentially
		// having multiple handlers. Without targetSubscriberId, autoBroadcast would go to all listeners, which isn't correct (ambiguity causes duplicate triggering).
		//
		// For a specific example, if the app adds a second global listener for any context (i.e. "*") then only that one new listener should trigger
		// when the corresponding autoBroadcast message arrives with the latest context; targetSubscriberId ensures this by uniquely identifying
		// the listener and it corresponding handler.
		if (message.meta.targetSubscriberId) {
			let targetHandlerSet;

			// Find the subscriber's handler and save in targetHandlerSet. (Alternatively, could have created a direct mapping, but reusing the existing data structure.)
			// This code can be converted to flatMap now that we have ES2020 support.
			Object.values(this.#contextHandlers).every((handlersByType) => {
				targetHandlerSet = handlersByType.find(
					(handlerSet) => handlerSet.targetSubscriberId === message.meta.targetSubscriberId
				);
				return targetHandlerSet === undefined;
			});

			// there is only one handler for the autoBroadcast path, but next section of code expects an array for the general case.
			handlers = targetHandlerSet ? [targetHandlerSet] : [];
		} else {
			// get all registered handlers, for this context type or global handlers
			handlers = (this.#contextHandlers[context?.type] ?? []).concat(this.#contextHandlers["*"] ?? []);
		}

		handlers.forEach(({ handler, channel }) => {
			// If broadcast channel matches our handler's channel, or
			// If broadcast is global and we have a generic handler
			if (message.meta.channel === channel || (!message.meta.channel && !channel)) {
				Logger.system.debug(
					`FDC3 Client: triggerContextListeners for all on ${channel ?? "default"} channel`,
					message.type,
					message
				);
				// invoke the addContextListener handler
				handler(context);
			} else {
				Logger.system.debug(
					`FDC3 Client: triggerContextListeners skipped for all on ${channel ?? "default"} channel`,
					message.type,
					message
				);
			}
		});
	}

	private triggerIntentListeners(message: InteropIntent) {
		const { intent, context } = message.payload;
		const handlers = this.#intentHandlers[intent];
		const functionArray = handlers ? handlers : [];
		if (context) functionArray.forEach((func: ContextHandler) => func(context));
	}

	private handleIncoming(message: InteropMessage) {
		Logger.system.debug("FDC3 Client: handleIncoming", message.type, message);

		switch (message.type) {
			case "broadcast":
				this.triggerContextListeners(message);
				break;
			case "intent":
				this.triggerIntentListeners(message);
				break;
			case "heartbeat":
				this.handleHeartbeat();
				break;
			default:
				break;
		}
	}

	/**
	 * Releases the queue, sending API calls to the interop service that had been deferred until a connection
	 * was established.
	 */
	private releaseQueue() {
		/**
		 * When messages were queued, the uuid would not have been available (uuid is based on windowName
		 * which is only available after startup.onReady). Now that we're releasing the queue, update
		 * the uuid in any queued messages before sending them out.
		 */
		const updateUUID = (message: InteropMessage): InteropMessage => {
			if (message?.meta?.uuid !== undefined) {
				message.meta.uuid = this.#uuid;
			}
			return message;
		};
		this.#queue.forEach(async (entry) => {
			const { message, handler, resolve } = entry;
			const { err, receiveMessage } = await this.exchange(updateUUID(message), handler);
			resolve({ err, receiveMessage });
		});
		this.#queue = [];
	}

	/**
	 * This is the central point for exchanging data with the interop service. Even though some FDC3
	 * calls such as broadcast() are one way, we always perform a query/response with the server, at
	 * least getting an empty response to know that it was handled.
	 */
	private exchange(
		message: InteropMessage,
		handler: StandardCallback = (): void => {}
	): Promise<{ err?: StandardError; receiveMessage?: InteropMessage }> {
		return new Promise(async (resolve) => {
			if (this.#connected) {
				const { err, response } = await this.#routerClient.query(SERVER_CHANNEL, message, handler);

				const receiveMessage: InteropMessage = response?.data;
				resolve({ err, receiveMessage });
			} else {
				this.#queue.push({ message, handler, resolve });
				this.start();
			}
		});
	}

	/**
	 * Performs the handshake to the interop service and starts hearbeats.
	 * @internal
	 */
	private async connect() {
		/**
		 * StartupData contains the AuthenticationToken necessary to connect to the interop service. When an app
		 * is spawned by Finsemble, the AuthenticationToken is automatically created by the launcher. When an app
		 * is freestanding, the AuthenticationToken is generated when a developer calls FSBLJSAdapter.startApp with a
		 * digital signing key. If an app can't be authenticated then Startup ensures that an anonymous
		 * AuthenticationToken exists (type "unauthenticated").
		 */
		const startupData = this.#startup.getStartupData();
		const { windowName = "Unknown window", authenticationToken } = startupData;
		Logger.system.log(`fdc3.connect StartupData for ${windowName}`, startupData);
		this.#uuid = `fdc3-${authenticationToken?.meta?.name}-${uuidv4()}`;

		const registerClient: InteropRegister = {
			type: "register",
			meta: this.meta(),
			payload: {
				// It would be better if the server assigned the client channel name. We could then protect against clashes.
				clientChannel: this.#clientChannel,
				// Ideally the server gets/verifies the windowName from the router
				windowName,
				// Required by InteropService to authenticate this client (preventing spoofing)
				authenticationToken,
			},
		};

		/**
		 * We need to ensure that the SERVER_CHANNEL responder has been added to the router. This happens
		 * at the kernel stage when the InteropService is starting up. Therefore we need to wait until the
		 * kernel stage is complete (i.e. InteropService is up and running) before we can query the SERVER_CHANNEL.
		 *
		 * Freestanding apps will periodically try to register with Interop if they are started prior to Finsemble
		 * coming up which would cause interop registration request being fired before the responder was added to the router.
		 *
		 * This waitForBootStage works, even though Finsemble might not be up, for two reasons:
		 * 	1) the underlying router subscribe in waitForBootStage will be queued by the routerClient until the router is up
		 * 	2) the underlying pubsub topic relies on a default pubSub responder in the router service, so it will immediately
		 * 		be available when the router is available.
		 */
		SystemManagerClient.waitForBootStage("kernel", "stageCompleted", (error) => {
			if (error) {
				Logger.system.error("connect error waitingForBootStage", error);
			} else {
				this.#routerClient.query(SERVER_CHANNEL, registerClient, (err) => {
					if (err) {
						Logger.system.error("connect error", err);
					} else {
						this.#routerClient.addResponder(this.#clientChannel, (err2, request) => {
							if (err2 || !request) {
								const errorString = errorAsString(err2 ?? "No request received on responder");
								Logger.system.error(
									`FinsembleDesktopAgent can't listen on channel ${this.#clientChannel} : ${errorString}`
								);
								// channel isn't available, uh oh
							} else {
								if (request.data) this.handleIncoming(request.data);
								request.sendQueryResponse(null);
							}
						});
						this.#connected = true;
						Logger.system.log(`FDC3 client connected for window ${windowName}`);
						this.releaseQueue();
						// The heartbeat monitor shouldn't start until now that we've successfully connected to the interop service
						this.#heartbeatMonitor.start();
					}
				});
			}
		});
	}

	/**
	 * Connect to the interop service. This method can be safely called multiple times. It will only
	 * establish a single connection.
	 * This function is called by FSBLDesktop and FSBLFreestanding.
	 * @internal
	 */
	async start() {
		// We only need to register with the FSBL event handlers once. Prevents connecting multiple times.
		if (this.#startHasBeenCalled) return;
		this.#startHasBeenCalled = true;

		await this.#startup.onReady();
		// The connect method performs the handshake and starts the heartbeats.
		// **We do not want to await** it since any calling function would stall, until a connection is established. In this
		// instance this is a "fire and forget" action and any connections issues will be highlighted by other API calls.
		this.connect();
	}

	/**
	 * Stops the client and puts it back into queuing state. The client
	 * can be restarted by calling connect().
	 * @internal
	 */
	async stop() {
		this.#heartbeatMonitor.stop();
		const unregisterClient: InteropUnregister = {
			type: "unregister",
			meta: this.meta(),
		};
		await this.exchange(unregisterClient);
		this.#startHasBeenCalled = false;
		this.#routerClient.removeResponder(this.#clientChannel);
		this.#connected = false;
	}

	/**
	 * Registers handlers which will be called if the client is disconnected from Finsemble. This is only useful when
	 * running in freestanding apps that might continue running after Finsemble has been terminated. Users should be
	 * prompted to restart their freestanding app when a disconnect is received.
	 *
	 * @param cb Handler function.
	 * @experimental This method is not part of the FDC3 standard and may be replaced in the future with alternative mechanisms.
	 */
	onDisconnect(cb: (error: string) => void) {
		this.#heartbeatMonitor.onDisconnect(cb);
	}

	/** @internal */
	async channelGetCurrentContext(channel: string, contextType?: string): Promise<Context | null | undefined> {
		const message: InteropCurrentContextRequest = {
			type: "currentContextRequest",
			meta: {
				...this.meta(),
				channel,
			},
			payload: {
				contextType,
			},
		};
		const { err, receiveMessage } = await this.exchange(message);
		if (err) return null;

		return receiveMessage?.type === "currentContext" ? receiveMessage.payload.context : null;
	}

	/**
	 * Publishes context to other apps on the desktop.
	 * Calling broadcast at the DesktopAgent scope will push the context to whatever Channel the app is joined to.
	 * If the app is not currently joined to a channel, calling fdc3.broadcast will have no effect.
	 * Apps can still directly broadcast and listen to context on any channel via the methods on the Channel class.
	 * @param context
	 */
	broadcast(context: Context) {
		if (!context) {
			throw new Error("Invalid arguments. The required context parameter is missing.");
		}
		const message: InteropBroadcast = {
			type: "broadcast",
			meta: this.meta(),
			payload: {
				context,
			},
		};
		this.exchange(message);
	}

	/** @internal */
	channelBroadcast(channel: string, context: Context) {
		const message: InteropBroadcast = {
			type: "broadcast",
			meta: {
				...this.meta(),
				channel,
			},
			payload: {
				context,
			},
		};
		this.exchange(message);
	}

	/**
	 * Retrieves a list of the System channels available for the app to join.
	 * This should include the 'global' channel.
	 */
	async getSystemChannels(): Promise<Channel[]> {
		const message: InteropSystemChannelRequest = {
			type: "systemChannelRequest",
			meta: this.meta(),
		};
		const { err, receiveMessage } = await this.exchange(message);
		if (err) return [];

		const channelNames = receiveMessage?.type === "systemChannels" ? receiveMessage?.payload?.channels : [];

		return channelNames.map((name) => new ChannelProxy(this, name, "system"));
	}

	/**
	 * Launches/links to an app by name.
	 * If a Context object is passed in, this object will be provided to the opened application via a contextListener.
	 * The Context argument is functionally equivalent to opening the target app with no context and broadcasting the context directly to it.
	 * If opening errors, it returns an Error with a string from the OpenError enumeration.
	 *
	 * @param target
	 * @param context
	 */
	async open(target: TargetApp, context?: Context): Promise<void> {
		if (!target) {
			throw new Error("Invalid arguments. The required target parameter is missing.");
		}
		const message: InteropOpenRequest = {
			type: "open",
			meta: this.meta(),
			payload: {
				target,
				context,
			},
		};
		const { err, receiveMessage } = await this.exchange(message);
		const interopOpenResponse = <InteropOpenResponse>receiveMessage;
		if (err) {
			throw this.fdc3Error(OpenError.ResolverUnavailable);
		} else if (interopOpenResponse.payload.error) {
			throw this.fdc3Error(interopOpenResponse.payload.error);
		}
	}

	/**
	 * Find out more information about a particular intent by passing its name, and optionally its context.
	 *
	 * findIntent is effectively granting programmatic access to the Desktop Agent's resolver.
	 * A promise resolving to the intent, its metadata and metadata about the apps that registered it is returned.
	 * This can be used to raise the intent against a specific app.
	 *
	 * If the resolution fails, the promise will return an `Error` with a string from the `ResolveError` enumeration.
	 *
	 * @param intent
	 * @param context
	 */
	async findIntent(intent: string, context?: Context): Promise<AppIntent> {
		if (!intent) {
			throw new Error("Invalid arguments. The required intent parameter is missing.");
		}
		const message: InteropFindIntentRequest = {
			type: "findIntent",
			meta: this.meta(),
			payload: {
				intent,
				context,
			},
		};
		const { err, receiveMessage } = await this.exchange(message);
		const interopFindIntentResponse = <InteropFindIntentResponse>receiveMessage;

		if (err) {
			throw this.fdc3Error(ResolveError.ResolverUnavailable);
		} else if (interopFindIntentResponse.payload.error) {
			throw this.fdc3Error(interopFindIntentResponse.payload.error);
		} else if (!interopFindIntentResponse.payload.appIntent) {
			throw this.fdc3Error("Invalid response returned from InteropService for findIntent. No appIntent and no error.");
		}

		return interopFindIntentResponse.payload.appIntent;
	}

	/**
	 * Find all the available intents for a particular context.
	 *
	 * findIntents is effectively granting programmatic access to the Desktop Agent's resolver.
	 * A promise resolving to all the intents, their metadata and metadata about the apps that registered it is returned,
	 * based on the context types the intents have registered.
	 *
	 * If the resolution fails, the promise will return an `Error` with a string from the `ResolveError` enumeration.
	 * @param context
	 */
	async findIntentsByContext(context: Context): Promise<Array<AppIntent>> {
		if (!context) {
			throw new Error("Invalid arguments. The required context parameter is missing.");
		}
		// The context parameter was made optional to support a query for all intents. This is consistent with findIntent, however it is not consistent with FDC3 spec plus.
		// a mechanism is still needed to query for contexts. A better solution is to add two new InteropAdmin methods; e.g. getIntents() and getContexts().
		const message: InteropFindIntentsByContext = {
			type: "findIntentsByContext",
			meta: this.meta(),
			payload: {
				context,
			},
		};

		const { receiveMessage } = await this.exchange(message);
		const interopIntentsList = <InteropIntentsList>receiveMessage;

		if (interopIntentsList.payload.error) {
			throw this.fdc3Error(interopIntentsList.payload.error);
		}
		return interopIntentsList.payload.intents || [];
	}

	/**
	 * Raises an intent to the desktop agent to resolve.
	 * @param intent
	 * @param context
	 * @param target
	 */
	async raiseIntent(intent: string, context: Context, target?: TargetApp): Promise<IntentResolution> {
		if (!intent) {
			throw new Error("Invalid arguments. The required intent parameter is missing.");
		}
		if (!context) {
			throw new Error("Invalid arguments. The required context parameter is missing.");
		}

		const message: InteropRaiseIntent = {
			type: "raiseIntent",
			meta: this.meta(),
			payload: {
				intent,
				context,
				target,
			},
		};
		const { err, receiveMessage } = await this.exchange(message);
		const interopIntentResolution = <InteropRaiseIntentResponse>receiveMessage;

		if (err) {
			throw this.fdc3Error(ResolveError.ResolverUnavailable);
		} else if (interopIntentResolution.payload.error) {
			throw this.fdc3Error(interopIntentResolution.payload.error);
		} else if (!interopIntentResolution.payload.data) {
			throw this.fdc3Error("Invalid response returned from InteropService for raiseIntent. No data.");
		}

		return interopIntentResolution.payload.data;
	}

	/**
	 * Finds and raises an intent against a target app based purely on context data.
	 *
	 * raiseIntentForContext is essentially a raiseIntent without a specific intent specified, hence the same "raiseIntent" Interop message is used.
	 *
	 * @param context
	 * @param target
	 */
	async raiseIntentForContext(context: Context, target?: TargetApp): Promise<IntentResolution> {
		if (!context) {
			throw new Error("Invalid arguments. The required context parameter is missing.");
		}

		const message: InteropRaiseIntent = {
			type: "raiseIntent",
			meta: this.meta(),
			payload: {
				context,
				target,
			},
		};
		const { err, receiveMessage } = await this.exchange(message);
		const interopRaiseIntentResponse = <InteropRaiseIntentResponse>receiveMessage;
		if (err) {
			throw this.fdc3Error(ResolveError.ResolverUnavailable);
		} else if (interopRaiseIntentResponse.payload.error) {
			throw this.fdc3Error(interopRaiseIntentResponse.payload.error);
		} else if (!interopRaiseIntentResponse.payload.data) {
			throw this.fdc3Error("Invalid response returned from InteropService for raiseIntentForContext. No data.");
		}

		return interopRaiseIntentResponse.payload.data;
	}

	/**
	 * Adds a listener for incoming Intents from the Agent.
	 * @param intent
	 * @param handler
	 */
	addIntentListener(intent: string, handler: ContextHandler): Listener {
		if (!handler) {
			throw new Error("Invalid arguments. The required intent parameter is missing.");
		}
		if (!handler) {
			throw new Error("Invalid arguments. The required handler parameter is missing.");
		}

		this.#intentHandlers[intent] = mmAdd(this.#intentHandlers, intent, handler);

		const message: InteropSubscribeIntent = {
			type: "subscribeIntent",
			meta: this.meta(),
			payload: {
				intent,
			},
		};
		this.exchange(message); // don't wait for response

		return {
			unsubscribe: () => {
				const unsubscribeMessage: InteropUnsubscribeIntent = {
					type: "unsubscribeIntent",
					meta: this.meta(),
					payload: {
						intent,
					},
				};

				this.exchange(unsubscribeMessage); // don't wait for response
				this.#intentHandlers[intent] = mmRemove(this.#intentHandlers, intent, handler);
			},
		};
	}

	/** @internal */
	channelAddContextListener(
		channel: string | null,
		contextTypeOrHandler: string | null | ContextHandler,
		handler?: ContextHandler
	): Listener {
		// Derives a definitive contextType and handler from the varargs
		// Possibilities: addContextListener(handler) - deprecated 1.1, addContextListener(null, handler), addContextListener(contextType, handler)
		const contextTypeOrWildCard = contextTypeOrHandler ? (contextTypeOrHandler as string) : "*";
		const contextType: string = handler ? contextTypeOrWildCard : "*";

		if (!handler) handler = contextTypeOrHandler as ContextHandler;

		// A unique targetSubscriberId must be passed in InteropSubscribeContext; the InteropService can direct messages to this specific listener by returning the targetSubscriberId in a broadcast message,
		const targetSubscriberId = `subscriberId.${Date.now()}_${Math.random()}`;

		// Bind targetSubscriberId to the specific handler and save in map
		const handlerSet = { handler, channel, targetSubscriberId };
		this.#contextHandlers[contextType] = mmAdd(this.#contextHandlers, contextType, handlerSet);

		let message: InteropSubscribeContext = {
			type: "subscribeContext",
			meta: { ...this.meta(), targetSubscriberId },
			payload: {
				contextType,
			},
		};
		if (channel) message.meta.channel = channel;

		this.exchange(message); // Don't wait for response

		return {
			unsubscribe: () => {
				let unsubscribeMessage: InteropUnsubscribeContext = {
					type: "unsubscribeContext",
					meta: this.meta(),
					payload: {
						contextType,
					},
				};
				if (channel) unsubscribeMessage.meta.channel = channel;

				this.#contextHandlers[contextType] = mmRemove(this.#contextHandlers, contextType, handlerSet);
				this.exchange(unsubscribeMessage); // Don't wait for response
			},
		};
	}

	/**
	 * Adds a listener for incoming context broadcast from the Desktop Agent.
	 * If the consumer is only interested in a context of a particular type, they can use the relevant overload that allows the type to be specified.
	 * @param contextTypeOrHandler
	 * @param handler
	 */
	addContextListener(contextTypeOrHandler: string | null | ContextHandler, handler?: ContextHandler): Listener {
		if (contextTypeOrHandler === undefined) {
			throw new Error("Invalid arguments. The contextTypeOrHandler parameter shouldn't be undefined.");
		}

		if (typeof contextTypeOrHandler !== "function" && !handler) {
			throw new Error("Invalid arguments. The required handler parameter is missing.");
		}

		return this.channelAddContextListener(null, contextTypeOrHandler, handler);
	}

	/**
	 * Joins the app to the specified channel.
	 * If an app is joined to a channel, all fdc3.broadcast calls will go to the channel, and all listeners assigned via fdc3.addContextListener will listen on the channel.
	 * An app can only be joined to one channel at a time.
	 * Rejects with error if the channel is unavailable or the join request is denied.
	 * Error with a string from the ChannelError enumeration.
	 * @param channelId
	 */
	async joinChannel(channelId: string): Promise<void> {
		if (!channelId) {
			throw new Error("Invalid arguments. The required channelId parameter is missing.");
		}
		const message: InteropJoinChannels = {
			type: "joinChannels",
			meta: this.meta(),
			payload: {
				channels: [channelId],
			},
		};
		await this.exchange(message);
	}

	/**
	 * Returns a Channel object for the specified channel, creating it (as an App channel) - if it does not exist.
	 * Error with a string from the ChannelError enumeration if channel could not be created or access was denied.
	 * @param channelId
	 */
	async getOrCreateChannel(channelId: string): Promise<Channel> {
		if (!channelId) {
			throw new Error("Invalid arguments. The required channelId parameter is missing.");
		}
		return new ChannelProxy(this, channelId);
	}

	/**
	 * FDC3 2.0 pre-release
	 *
	 * Get all joined channels
	 *
	 * Known race condition: getCurrentChannels() can return an empty string if it is called before the InteropService's linker module
	 * has completed initializing the app. This can occur if fdc3.getCurrentChannels() is the very first call that an app makes. To work around
	 * this issue, use the interop.autoConnect config flag. A future version of the InteropService will hold off on completed registrations
	 * until submodules have finished their work.
	 */
	async getCurrentChannels(): Promise<Channel[] | null> {
		const message: InteropGetCurrentChannels = {
			type: "getCurrentChannels",
			meta: this.meta(),
		};
		const { err, receiveMessage } = await this.exchange(message);
		const interopGetCurrentChannelsResponse = <InteropGetCurrentChannelsResponse>receiveMessage;
		if (err) {
			throw this.fdc3Error(<string>err);
		} else if (interopGetCurrentChannelsResponse.payload.error) {
			throw this.fdc3Error(interopGetCurrentChannelsResponse.payload.error);
		} else if (!interopGetCurrentChannelsResponse.payload.data) {
			throw this.fdc3Error("Invalid response returned from InteropService for getCurrentChannels. No data.");
		}

		return interopGetCurrentChannelsResponse.payload.data.map(
			(entry) => new ChannelProxy(this, entry.channel, "system")
		);
	}

	/**
	 * Returns the Channel object for the current channel membership.
	 * Returns null if the app is not joined to a channel.
	 */
	async getCurrentChannel(): Promise<Channel | null> {
		const channels = await this.getCurrentChannels();
		return channels && channels.length ? channels[0] : null;
	}

	/**
	 * Removes the app from any channel membership.
	 * Context broadcast and listening through the top-level fdc3.broadcast and fdc3.addContextListener will be in a no-op when the app is not on a channel.
	 */
	async leaveCurrentChannel(): Promise<void> {
		const message: InteropLeaveChannels = {
			type: "leaveChannels",
			meta: this.meta(),
			payload: {},
		};
		await this.exchange(message);
	}

	/**
	 * Retrieves information about the FDC3 Desktop Agent implementation, such as
	 * the implemented version of the FDC3 specification and the name of the implementation
	 * provider.
	 */
	getInfo(): ImplementationMetadata {
		return {
			fdc3Version: "1.2",
			provider: "Finsemble",
			// This data is added at build time by webpack EnvironmentPlugin
			providerVersion: this.#FSBLVersion,
		};
	}
}
